<script setup lang="ts">
import { computed, ref, watch } from '#imports'
import { onClickOutside } from '@vueuse/core'
import { FEATURE_PAGES } from '~/constants/featurePages'
import { UK_TOOLS } from '~/constants/ukTools'
import { useI18n } from 'vue-i18n'

/**
 * "Features" entry of the main menu. On desktop it is a hover trigger with a
 * full-width mega-menu teleported to <body> (so it can span the viewport
 * under the sticky header); below the `nav` breakpoint it is an accordion row
 * inside the off-canvas menu. Both list the same FEATURE_PAGES.
 */
const { t, locale } = useI18n()
/* The Civil Service and NHS writers exist in English only: listed in the English menu alone. */
const ukTools = computed(() => (locale.value === 'en' ? UK_TOOLS : []))
const router = useRouter()
const localize = useLocalizedPath()

interface FeatureItem {
  id: string
  title: string
  description: string
  /** Real route: every feature has its own page now (see constants/featurePages.ts). */
  path: string
  icon: string
}

const props = defineProps<{
  isDesktop: boolean
  isOpen?: boolean
}>()

const emit = defineEmits<{
  'update:isOpen': [value: boolean]
  close: []
}>()

// Desktop dropdown state
const isDropdownOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
const timeoutId = ref<NodeJS.Timeout | null>(null)

// Mobile accordion state
const isMobileAccordionOpen = computed({
  get: () => props.isOpen ?? false,
  set: (value) => emit('update:isOpen', value)
})

const features = computed<FeatureItem[]>(() => FEATURE_PAGES.map(page => ({
  id: page.id,
  title: t(page.titleKey),
  description: t(page.descriptionKey),
  path: page.path,
  icon: page.icon,
})))

// Desktop hover handlers
const handleMouseEnter = () => {
  if (!props.isDesktop) return

  if (timeoutId.value) {
    clearTimeout(timeoutId.value)
    timeoutId.value = null
  }

  isDropdownOpen.value = true
}

const handleMouseLeave = () => {
  if (!props.isDesktop) return

  timeoutId.value = setTimeout(() => {
    isDropdownOpen.value = false
  }, 150)
}

// Mobile accordion handler
const toggleMobileAccordion = () => {
  if (props.isDesktop) return
  isMobileAccordionOpen.value = !isMobileAccordionOpen.value
}

// Navigate to a feature page
/* Close the menu on following one of the UK links (they are plain links, not router pushes). */
const closeMenu = () => {
  isDropdownOpen.value = false
  isMobileAccordionOpen.value = false
  emit('close')
}

const handleFeatureClick = (feature: FeatureItem) => {
  isDropdownOpen.value = false
  isMobileAccordionOpen.value = false
  emit('close')
  router.push(localize(feature.path))
}

// Click outside to close dropdown
if (import.meta.client) {
  onClickOutside(dropdownRef, () => {
    if (props.isDesktop && isDropdownOpen.value) {
      isDropdownOpen.value = false
    }
  })
}

// ESC key to close
const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    isDropdownOpen.value = false
    isMobileAccordionOpen.value = false
  }
}

// Watch for ESC key
if (import.meta.client) {
  watch([isDropdownOpen, isMobileAccordionOpen], ([dropdownOpen, accordionOpen]) => {
    if (dropdownOpen || accordionOpen) {
      document.addEventListener('keydown', handleKeyDown)
    } else {
      document.removeEventListener('keydown', handleKeyDown)
    }
  })
}
</script>

<template>
  <!-- Desktop trigger: same look as the other menu links in AppNavbar -->
  <div
    v-if="isDesktop"
    class="relative"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <a
      href="/#features"
      class="inline-flex items-center gap-1 whitespace-nowrap font-body text-base font-semibold text-ink transition-colors duration-300 hover:text-accent dark:text-white dark:hover:text-accent-soft"
      :aria-expanded="isDropdownOpen"
      @click.prevent="handleFeatureClick({ id: 'features', title: '', description: '', path: '/#features', icon: 'bi-collection' })"
    >
      {{ t('homepage.featureOverview.features') }}
      <i
        class="bi bi-chevron-down text-xs transition-transform duration-300"
        :class="{ 'rotate-180': isDropdownOpen }"
        aria-hidden="true"
      />
    </a>
  </div>

  <!-- Desktop mega-menu, teleported to body so it can span the full width under the header -->
  <Teleport v-if="isDesktop" to="body">
    <div
      ref="dropdownRef"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <Transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <!-- `.sofax` re-opens the design-system scope: the panel lives outside the layout -->
        <div
          v-if="isDropdownOpen"
          class="sofax fixed left-0 top-[72px] z-40 w-full border-y border-line bg-white shadow-lift dark:border-gray-800 dark:bg-gray-900"
        >
          <div class="shell py-8">
            <!-- Feature cards, in the template's service-card shape -->
            <div class="grid grid-cols-3 gap-4">
              <button
                v-for="feature in features"
                :key="feature.id"
                type="button"
                class="group flex items-start gap-4 rounded-[0.625rem] border border-transparent p-4 text-left transition-all duration-300 hover:border-line hover:bg-surface dark:hover:border-gray-700 dark:hover:bg-gray-800"
                @click="handleFeatureClick(feature)"
              >
                <SofaxIconTile :icon="feature.icon" size="sm" />
                <div class="min-w-0">
                  <div class="font-title text-lg font-semibold leading-tight text-ink transition-colors duration-300 group-hover:text-accent dark:text-white">
                    {{ feature.title }}
                  </div>
                  <div class="mt-1 text-sm leading-relaxed text-muted dark:text-gray-400">
                    {{ feature.description }}
                  </div>
                </div>
              </button>
            </div>

            <!-- For UK applicants: the Civil Service and NHS writers (English menu only) -->
            <div v-if="ukTools.length" class="mt-6 border-t border-line pt-6 dark:border-gray-800">
              <div class="mb-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted dark:text-gray-400">For UK applicants</div>
              <div class="grid grid-cols-3 gap-4">
                <NuxtLink
                  v-for="tool in ukTools"
                  :key="tool.id"
                  :to="tool.path"
                  class="group flex items-start gap-4 rounded-[0.625rem] border border-transparent p-4 text-left transition-all duration-300 hover:border-line hover:bg-surface dark:hover:border-gray-700 dark:hover:bg-gray-800"
                  @click="closeMenu"
                >
                  <SofaxIconTile :icon="tool.icon" size="sm" />
                  <div class="min-w-0">
                    <div class="font-title text-lg font-semibold leading-tight text-ink transition-colors duration-300 group-hover:text-accent dark:text-white">
                      {{ tool.title }}
                    </div>
                    <div class="mt-1 line-clamp-2 text-sm leading-relaxed text-muted dark:text-gray-400">
                      {{ tool.description }}
                    </div>
                  </div>
                </NuxtLink>
              </div>
            </div>

            <!-- Footer CTA row -->
            <div class="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6 dark:border-gray-800">
              <div class="text-base text-muted dark:text-gray-400">
                {{ t('nav.featuresDropdown.ctaSubtitle') }}
              </div>
              <SofaxBtn
                to="/free-cv-builder"
                :label="t('nav.featuresDropdown.ctaButton')"
                variant="accent"
                class="btn-sm"
                @click="isDropdownOpen = false"
              />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>

  <!-- Mobile accordion row, matching the off-canvas links in AppNavbar -->
  <div v-else class="w-full">
    <button
      type="button"
      class="flex w-full items-center justify-between py-4 font-title text-lg font-semibold text-ink dark:text-white"
      :aria-expanded="isMobileAccordionOpen"
      @click="toggleMobileAccordion"
    >
      {{ t('homepage.featureOverview.features') }}
      <i
        class="bi bi-chevron-down text-base text-muted transition-transform duration-300"
        :class="{ 'rotate-180': isMobileAccordionOpen }"
        aria-hidden="true"
      />
    </button>

    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="max-h-0 opacity-0"
      enter-to-class="max-h-[2000px] opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="max-h-[2000px] opacity-100"
      leave-to-class="max-h-0 opacity-0"
    >
      <ul v-if="isMobileAccordionOpen" class="overflow-hidden pb-4">
        <li v-for="feature in features" :key="feature.id">
          <button
            type="button"
            class="flex w-full items-start gap-3 rounded-[0.625rem] p-3 text-left transition-colors duration-300 hover:bg-surface active:bg-surface dark:hover:bg-gray-800 dark:active:bg-gray-800"
            @click="handleFeatureClick(feature)"
          >
            <i :class="['bi', feature.icon, 'mt-0.5 shrink-0 text-xl text-accent']" aria-hidden="true" />
            <div class="min-w-0">
              <div class="font-body text-base font-semibold text-ink dark:text-white">
                {{ feature.title }}
              </div>
              <div class="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted dark:text-gray-400">
                {{ feature.description }}
              </div>
            </div>
          </button>
        </li>
        <!-- For UK applicants (English menu only) -->
        <li v-if="ukTools.length" class="mt-2 border-t border-line px-3 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-muted dark:border-gray-800 dark:text-gray-400">
          For UK applicants
        </li>
        <li v-for="tool in ukTools" :key="tool.id">
          <NuxtLink
            :to="tool.path"
            class="flex w-full items-start gap-3 rounded-[0.625rem] p-3 text-left transition-colors duration-300 hover:bg-surface active:bg-surface dark:hover:bg-gray-800 dark:active:bg-gray-800"
            @click="closeMenu"
          >
            <i :class="['bi', tool.icon, 'mt-0.5 shrink-0 text-xl text-accent']" aria-hidden="true" />
            <div class="min-w-0">
              <div class="font-body text-base font-semibold text-ink dark:text-white">
                {{ tool.title }}
              </div>
              <div class="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted dark:text-gray-400">
                {{ tool.description }}
              </div>
            </div>
          </NuxtLink>
        </li>
      </ul>
    </Transition>
  </div>
</template>
